import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate, Link } from 'react-router-dom'
import { supabase, Vehicle, TowJob, Claim } from '../lib/supabase'
import { formatKES } from '../types'
import { logActivity, logPageView } from '../lib/activityLogger'
import {
  Home, MapPin, Package, FileText, User, Truck, Battery, Disc, Fuel,
  Shield, Phone, MessageSquare, CheckCircle, LogOut, Car, Navigation,
  Star, Settings, Store, Plus, Wrench, Clock, AlertCircle, CreditCard,
  Bell, TrendingUp, BarChart3, Activity, ArrowRight, X, Menu, ChevronDown,
  Search, ShoppingBag, Loader2
} from 'lucide-react'

type Tab = 'home' | 'vehicles' | 'jobs' | 'orders' | 'marketplace' | 'transactions' | 'claims' | 'settings'

export default function CustomerDashboard() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('home')
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [jobs, setJobs] = useState<TowJob[]>([])
  const [claims, setClaims] = useState<Claim[]>([])
  const [partsOrders, setPartsOrders] = useState<any[]>([])
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [orderItems, setOrderItems] = useState<any[]>([])
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [ordersPage, setOrdersPage] = useState(1)
  const ordersItemsPerPage = 10
  const [newVehicle, setNewVehicle] = useState({
    plate: '',
    make: '',
    model: '',
    year: '',
    color: '',
    insurance_policy_number: '',
    insurer_name: '',
    policy_active: true
  })

  useEffect(() => {
    if (!profile) return
    supabase.from('vehicles').select('*').eq('owner_id', profile.id).then(({ data }) => setVehicles((data as Vehicle[]) || []))
    supabase.from('tow_jobs').select('*').eq('customer_id', profile.id).order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
    supabase.from('claims').select('*').eq('customer_id', profile.id).order('created_at', { ascending: false }).then(({ data }) => setClaims((data as Claim[]) || []))
    supabase.from('part_orders').select('*').eq('customer_id', profile.id).order('created_at', { ascending: false }).then(({ data }) => setPartsOrders(data || []))
  }, [profile])

  // Reload parts orders when switching to orders tab
  useEffect(() => {
    if (tab === 'orders' && profile) {
      supabase.from('part_orders').select('*').eq('customer_id', profile.id).order('created_at', { ascending: false }).then(({ data }) => setPartsOrders(data || []))
    }
  }, [tab, profile])

  // Reset to page 1 when search query changes
  useEffect(() => {
    setOrdersPage(1)
  }, [searchQuery])

  // Filter orders based on search query
  const filteredOrders = partsOrders.filter(order => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      order.reference?.toLowerCase().includes(query) ||
      order.status?.toLowerCase().includes(query) ||
      order.delivery_address?.toLowerCase().includes(query)
    )
  })

  // Log page view when tab changes
  useEffect(() => {
    if (profile) {
      logPageView(`/customer/${tab}`)
    }
  }, [tab, profile])

  // Load order items when an order is selected
  useEffect(() => {
    if (selectedOrder) {
      supabase
        .from('part_order_items')
        .select('*')
        .eq('order_id', selectedOrder.id)
        .then(({ data }) => setOrderItems(data || []))
    }
  }, [selectedOrder])

  const activeJobs = jobs.filter(j => !['payment_completed', 'completed', 'cancelled'].includes(j.status))
  const completedJobs = jobs.filter(j => ['payment_completed', 'completed'].includes(j.status))
  const completedPartsOrders = claims.filter(c => ['delivered', 'completed'].includes(c.status))

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('sidebar-open')
    } else {
      document.body.classList.remove('sidebar-open')
    }
    return () => {
      document.body.classList.remove('sidebar-open')
    }
  }, [mobileMenuOpen])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Main</div>
            <div className={`sidebar-item ${tab === 'home' ? 'active' : ''}`} onClick={() => setTab('home')}>
              <Home size={18} />
              <span>Dashboard</span>
            </div>
            <div className={`sidebar-item ${tab === 'vehicles' ? 'active' : ''}`} onClick={() => setTab('vehicles')}>
              <Car size={18} />
              <span>My Vehicles</span>
            </div>
            <div className={`sidebar-item ${tab === 'jobs' ? 'active' : ''}`} onClick={() => setTab('jobs')}>
              <Truck size={18} />
              <span>Service Requests</span>
            </div>
            <div className={`sidebar-item ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>
              <Package size={18} />
              <span>Parts Orders</span>
            </div>
            <Link to="/parts-marketplace" className="sidebar-item" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Store size={18} />
              <span>Parts Shop</span>
            </Link>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">History</div>
            <div className={`sidebar-item ${tab === 'transactions' ? 'active' : ''}`} onClick={() => setTab('transactions')}>
              <CheckCircle size={18} />
              <span>Transactions</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Insurance</div>
            <div className={`sidebar-item ${tab === 'claims' ? 'active' : ''}`} onClick={() => setTab('claims')}>
              <Shield size={18} />
              <span>Claims</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={18} />
              <span>Settings</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-bottom-fixed">
          <div className="sidebar-footer">
            <div className="name">{profile?.full_name || 'User'}</div>
            <div className="role">{profile?.role || 'customer'}</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
        />
      )}

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)'
              }}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1>
                {tab === 'home' && 'Dashboard'}
                {tab === 'vehicles' && 'My Vehicles'}
                {tab === 'jobs' && 'Service Requests'}
                {tab === 'orders' && 'Parts Orders'}
                {tab === 'marketplace' && 'Parts Shop'}
                {tab === 'transactions' && 'Transactions'}
                {tab === 'claims' && 'Insurance Claims'}
                {tab === 'settings' && 'Settings'}
              </h1>
              <p className="subtitle">
                Welcome back, {profile?.full_name?.split(' ')[0] || 'User'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span>{profile?.full_name?.split(' ')[0] || 'User'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setTab('settings'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    onClick={() => { signOut(); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/towing-request')}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Truck size={18} />
              <span>Request Service</span>
            </button>
          </div>
        </header>

        <div className="dashboard-content">
          {tab === 'home' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                    <Truck size={24} />
                  </div>
                  <div className="stat-label">Active Requests</div>
                  <div className="stat-value">{activeJobs.length}</div>
                  <div className="stat-trend">
                    <Clock size={14} />
                    Currently in progress
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Completed Jobs</div>
                  <div className="stat-value">{completedJobs.length}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Total services
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Car size={24} />
                  </div>
                  <div className="stat-label">My Vehicles</div>
                  <div className="stat-value">{vehicles.length}</div>
                  <div className="stat-trend">
                    <Navigation size={14} />
                    Registered vehicles
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <Shield size={24} />
                  </div>
                  <div className="stat-label">Active Claims</div>
                  <div className="stat-value">{claims.filter(c => c.status === 'pending' || c.status === 'approved' || c.status === 'disputed').length}</div>
                  <div className="stat-trend">
                    <FileText size={14} />
                    Insurance claims
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <h2 className="card-title">Quick Services</h2>
                <div className="quick-services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                  {[
                    { icon: Truck, label: 'Towing', color: 'var(--red)', desc: 'Emergency towing service', type: 'towing' },
                    { icon: Battery, label: 'Battery', color: 'var(--blue)', desc: 'Jump-start or replacement', type: 'battery' },
                    { icon: Disc, label: 'Flat Tyre', color: 'var(--amber)', desc: 'Tyre repair or change', type: 'flat_tyre' },
                    { icon: Fuel, label: 'Fuel Delivery', color: 'var(--green)', desc: 'Emergency fuel delivery', type: 'fuel' },
                    { icon: Shield, label: 'Insurance', color: '#0d9488', desc: 'File insurance claim', type: 'insurance' },
                  ].map((service, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (service.type === 'insurance') {
                          setTab('claims')
                        } else {
                          navigate('/towing-request', { state: { preselectedService: service.type } })
                        }
                      }}
                      className="quick-service-card"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '1rem',
                        background: 'var(--bg)',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = service.color
                        e.currentTarget.style.transform = 'translateY(-4px)'
                        e.currentTarget.style.boxShadow = `0 4px 12px ${service.color}20`
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                      onTouchStart={(e) => {
                        e.currentTarget.style.borderColor = service.color
                        e.currentTarget.style.transform = 'scale(0.98)'
                      }}
                      onTouchEnd={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: service.color + '20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: service.color,
                      }}>
                        <service.icon size={24} />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.2rem', color: service.color }}>
                          {service.label}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                          {service.desc}
                        </div>
                        <div style={{ marginTop: '0.25rem', color: service.color, fontSize: '0.7rem', fontWeight: 600 }}>
                          Tap to request →
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {activeJobs.length > 0 && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h2 className="card-title">Active Service Requests</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {activeJobs.map(job => (
                      <div
                        key={job.id}
                        style={{
                          padding: '1.25rem',
                          background: 'var(--bg)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'var(--red-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--red)'
                          }}>
                            <Truck size={24} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>{job.reference}</div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                              {job.service_type.replace('_', ' ')} • {new Date(job.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/tracking/${job.id}`)}
                          className="btn btn-primary btn-sm"
                        >
                          Track
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'vehicles' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>My Vehicles</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowVehicleModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Plus size={18} />
                  Add Vehicle
                </button>
              </div>
              {vehicles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Car size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No vehicles registered yet</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowVehicleModal(true)}
                    style={{ marginTop: '1rem' }}
                  >
                    Register Your First Vehicle
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                  {vehicles.map(vehicle => (
                    <div
                      key={vehicle.id}
                      style={{
                        padding: '1.5rem',
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          background: 'var(--blue-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--blue)'
                        }}>
                          <Car size={28} />
                        </div>
                        {vehicle.policy_active && (
                          <span className="badge badge-green" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                            Insured
                          </span>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                          {vehicle.plate}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>
                          {vehicle.make} {vehicle.model} • {vehicle.year}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                          {vehicle.color}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'jobs' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Service Requests</h2>
              {jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Truck size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No service requests yet</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/towing-request')}
                    style={{ marginTop: '1rem' }}
                  >
                    Request Service
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {jobs.map(job => (
                    <div
                      key={job.id}
                      style={{
                        padding: '1.25rem',
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: job.status === 'payment_completed' ? 'var(--green-light)' : job.status === 'completed' ? 'var(--blue-light)' : 'var(--red-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: job.status === 'payment_completed' ? 'var(--green)' : job.status === 'completed' ? 'var(--blue)' : 'var(--red)'
                        }}>
                          <Truck size={24} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>{job.reference}</div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                            {job.service_type.replace('_', ' ')} • {new Date(job.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className={`badge ${
                          job.status === 'payment_completed' ? 'badge-green' :
                          job.status === 'completed' ? 'badge-blue' :
                          job.status === 'cancelled' ? 'badge-red' :
                          'badge-yellow'
                        }`}>
                          {job.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', marginTop: '0.25rem' }}>
                          {formatKES(job.total_amount)}
                        </div>
                        <button
                          onClick={() => navigate(`/tracking/${job.id}`)}
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: '0.5rem' }}
                        >
                          {job.status === 'payment_completed' || job.status === 'completed' ? 'View' : 'Track'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'orders' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', margin: 0 }}>Parts Orders</h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
                    <input
                      type="text"
                      placeholder="Search orders..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        padding: '0.5rem 1rem 0.5rem 2.5rem',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '0.875rem',
                        width: '250px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
              </div>
              {filteredOrders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Package size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>{searchQuery ? 'No orders found matching your search' : 'No parts orders yet'}</p>
                  {!searchQuery && (
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: '1rem' }}
                      onClick={() => navigate('/parts-marketplace')}
                    >
                      Browse Parts Marketplace
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {filteredOrders.slice((ordersPage - 1) * ordersItemsPerPage, ordersPage * ordersItemsPerPage).map(order => {
                      const statusColors = {
                        pending: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
                        confirmed: { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
                        processing: { bg: '#f3e8ff', text: '#6b21a8', border: '#9333ea' },
                        ready: { bg: '#cffafe', text: '#0e7490', border: '#06b6d4' },
                        delivered: { bg: '#dcfce7', text: '#166534', border: '#22c55e' }
                      }
                      const color = statusColors[order.status as keyof typeof statusColors] || statusColors.pending

                      const steps = [
                        { key: 'pending', label: 'Order Placed', icon: ShoppingBag },
                        { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
                        { key: 'processing', label: 'Processing', icon: Loader2 },
                        { key: 'ready', label: 'Ready', icon: Truck },
                        { key: 'delivered', label: 'Delivered', icon: Package }
                      ]
                      const currentStepIndex = steps.findIndex(s => s.key === order.status)

                      return (
                        <div
                          key={order.id}
                          style={{
                            padding: '1.5rem',
                            background: 'white',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}
                        >
                          {/* Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                              <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '12px',
                                background: color.bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: color.text
                              }}>
                                <Package size={28} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.25rem' }}>{order.reference}</div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--muted)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {order.delivery_address}
                                </div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                background: color.bg,
                                color: color.text,
                                border: `1px solid ${color.border}`
                              }}>
                                {order.status.replace('_', ' ')}
                              </span>
                              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1f2937', marginTop: '0.5rem' }}>
                                {formatKES(order.total_amount)}
                              </div>
                            </div>
                          </div>

                          {/* Progress Tracker */}
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', padding: '0 10px' }}>
                              {steps.map((step, index) => {
                                const Icon = step.icon
                                const isCompleted = index <= currentStepIndex
                                const isCurrent = index === currentStepIndex
                                return (
                                  <div
                                    key={step.key}
                                    style={{
                                      position: 'relative',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      flex: 1
                                    }}
                                  >
                                    <div style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '50%',
                                      background: isCompleted ? color.border : '#e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: isCompleted ? 'white' : '#9ca3af',
                                      border: isCurrent ? `3px solid ${color.border}` : 'none',
                                      zIndex: 1
                                    }}>
                                      <Icon size={18} />
                                    </div>
                                    <div style={{ fontSize: '0.7rem', fontWeight: 600, marginTop: '0.25rem', textAlign: 'center', color: isCompleted ? color.text : '#9ca3af' }}>
                                      {step.label}
                                    </div>
                                    {index < steps.length - 1 && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '18px',
                                        left: '50%',
                                        width: 'calc(100% - 20px)',
                                        height: '2px',
                                        background: isCompleted ? color.border : '#e5e7eb',
                                        zIndex: 0
                                      }} />
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          {/* Action Button */}
                          <button
                            onClick={() => {
                              setSelectedOrder(order)
                              setShowOrderDetails(true)
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              background: 'var(--red)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)' }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}
                          >
                            View Details
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      )
                    })}
                  </div>

                  {/* Pagination */}
                  {filteredOrders.length > ordersItemsPerPage && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                        Showing {((ordersPage - 1) * ordersItemsPerPage) + 1} to {Math.min(ordersPage * ordersItemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setOrdersPage(Math.max(1, ordersPage - 1))}
                          disabled={ordersPage === 1}
                          style={{
                            padding: '0.5rem 1rem',
                            background: ordersPage === 1 ? 'var(--bg)' : 'white',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            cursor: ordersPage === 1 ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            opacity: ordersPage === 1 ? 0.5 : 1
                          }}
                        >
                          Previous
                        </button>
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          {Array.from({ length: Math.ceil(filteredOrders.length / ordersItemsPerPage) }, (_, i) => i + 1).slice(0, 5).map((page) => (
                            <button
                              key={page}
                              onClick={() => setOrdersPage(page)}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: ordersPage === page ? 'var(--red)' : 'white',
                                border: ordersPage === page ? '1px solid var(--red)' : '1px solid var(--border)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                color: ordersPage === page ? 'white' : 'var(--text)'
                              }}
                            >
                              {page}
                            </button>
                          ))}
                          {Math.ceil(filteredOrders.length / ordersItemsPerPage) > 5 && <span style={{ padding: '0.5rem', color: 'var(--muted)' }}>...</span>}
                        </div>
                        <button
                          onClick={() => setOrdersPage(Math.min(Math.ceil(filteredOrders.length / ordersItemsPerPage), ordersPage + 1))}
                          disabled={ordersPage === Math.ceil(filteredOrders.length / ordersItemsPerPage)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: ordersPage === Math.ceil(filteredOrders.length / ordersItemsPerPage) ? 'var(--bg)' : 'white',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            cursor: ordersPage === Math.ceil(filteredOrders.length / ordersItemsPerPage) ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            opacity: ordersPage === Math.ceil(filteredOrders.length / ordersItemsPerPage) ? 0.5 : 1
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'transactions' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Transactions</h2>
              {completedJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <CheckCircle size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No completed transactions yet</p>
                </div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                    <tbody>
                      {completedJobs.map(job => (
                        <tr key={job.id}>
                          <td style={{ fontWeight: 600 }}>{job.reference}</td>
                          <td>{job.service_type === 'parts' ? 'Parts Order' : job.service_type.replace('_', ' ')}</td>
                          <td>
                            <span className={`badge badge-green`}>
                              Completed
                            </span>
                          </td>
                          <td>{new Date(job.created_at).toLocaleDateString()}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </td>
                          <td>
                            <button className="btn btn-primary btn-sm">View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {completedJobs.map(job => (
                      <div key={job.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Reference</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{job.reference}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Type</span>
                          <span className="data-table-card-value">{job.service_type === 'parts' ? 'Parts Order' : job.service_type.replace('_', ' ')}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className="badge badge-green">Completed</span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Date</span>
                          <span className="data-table-card-value">{new Date(job.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Amount</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>
                            KES {Number(job.total_amount).toLocaleString()}
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm">View</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'claims' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Insurance Claims</h2>
                <button className="btn btn-primary">
                  <Plus size={18} />
                  File New Claim
                </button>
              </div>
              {claims.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <Shield size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No insurance claims filed</p>
                  <button className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    File Your First Claim
                  </button>
                </div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Insurer</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                    <tbody>
                      {claims.map(claim => (
                        <tr key={claim.id}>
                          <td style={{ fontWeight: 600 }}>{claim.reference}</td>
                          <td>
                            <span className={`badge ${
                              claim.status === 'paid' ? 'badge-green' :
                              claim.status === 'approved' ? 'badge-blue' :
                              claim.status === 'flagged' ? 'badge-red' :
                              'badge-amber'
                            }`}>
                              {claim.status}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(claim.total_amount).toLocaleString()}
                          </td>
                          <td>{claim.insurer_name}</td>
                          <td>{new Date(claim.created_at).toLocaleDateString()}</td>
                          <td>
                            <button className="btn btn-primary btn-sm">View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile card version */}
                  <div className="data-table-card-wrapper" style={{ display: 'none' }}>
                    {claims.map(claim => (
                      <div key={claim.id} className="data-table-card">
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Reference</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>{claim.reference}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Status</span>
                          <span className="data-table-card-value">
                            <span className={`badge ${
                              claim.status === 'paid' ? 'badge-green' :
                              claim.status === 'approved' ? 'badge-blue' :
                              claim.status === 'flagged' ? 'badge-red' :
                              'badge-amber'
                            }`}>
                              {claim.status}
                            </span>
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Amount</span>
                          <span className="data-table-card-value" style={{ fontWeight: 600 }}>
                            KES {Number(claim.total_amount).toLocaleString()}
                          </span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Insurer</span>
                          <span className="data-table-card-value">{claim.insurer_name}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Date</span>
                          <span className="data-table-card-value">{new Date(claim.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="data-table-card-row">
                          <span className="data-table-card-label">Action</span>
                          <button className="btn btn-primary btn-sm">View</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Account Settings</h2>
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    defaultValue={profile?.full_name}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    defaultValue={profile?.phone}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Email</label>
                  <input
                    type="email"
                    className="form-input"
                    defaultValue={profile?.email || ''}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <button className="btn btn-primary">Save Changes</button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => setShowOrderDetails(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1f2937' }}>
                Order Details
              </h2>
              <button
                onClick={() => setShowOrderDetails(false)}
                style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Order Reference</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>{selectedOrder.reference}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Status</div>
                  <span className={`badge badge-${selectedOrder.status === 'delivered' ? 'green' : selectedOrder.status === 'processing' ? 'blue' : 'yellow'}`}>
                    {selectedOrder.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Total Amount</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1f2937' }}>{formatKES(selectedOrder.total_amount)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Delivery Method</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>{selectedOrder.delivery_method}</div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>Delivery Information</h3>
              <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <MapPin size={18} style={{ color: '#dc2626' }} />
                  <span style={{ fontSize: '0.95rem', color: '#374151' }}>{selectedOrder.delivery_address}</span>
                </div>
                {selectedOrder.delivery_notes && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Notes: {selectedOrder.delivery_notes}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>Order Items</h3>
              <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '1rem' }}>
                {orderItems.length === 0 ? (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', padding: '1rem' }}>
                    No items found
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {orderItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid var(--border)'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>
                            {item.part_name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            Qty: {item.quantity} × KES {item.unit_price.toLocaleString()}
                          </div>
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
                          KES {item.total_price.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowOrderDetails(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: 'var(--red)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Registration Modal */}
      {showVehicleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.5rem' }}>Register New Vehicle</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>License Plate</label>
                <input
                  type="text"
                  className="form-input"
                  value={newVehicle.plate}
                  onChange={(e) => setNewVehicle({ ...newVehicle, plate: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Make</label>
                <input
                  type="text"
                  className="form-input"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Model</label>
                <input
                  type="text"
                  className="form-input"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Year</label>
                <input
                  type="text"
                  className="form-input"
                  value={newVehicle.year}
                  onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Color</label>
                <input
                  type="text"
                  className="form-input"
                  value={newVehicle.color}
                  onChange={(e) => setNewVehicle({ ...newVehicle, color: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    supabase.from('vehicles').insert({
                      owner_id: profile!.id,
                      plate: newVehicle.plate,
                      make: newVehicle.make,
                      model: newVehicle.model,
                      year: newVehicle.year,
                      color: newVehicle.color
                    }).then(() => {
                      setShowVehicleModal(false)
                      setNewVehicle({ plate: '', make: '', model: '', year: '', color: '' })
                      supabase.from('vehicles').select('*').eq('owner_id', profile!.id).then(({ data }) => setVehicles((data as Vehicle[]) || []))
                    })
                  }}
                  style={{ flex: 1 }}
                >
                  Register Vehicle
                </button>
                <button
                  className="btn"
                  onClick={() => setShowVehicleModal(false)}
                  style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
